import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import { AUDIT_LOG_CUSTOM_RANGE } from "@/pages/app/provider/settings/practice-setting/practice-setting-constant";
import { useAuditLogList } from "@/queries/practice-setting/audit-log/use-audit-log-list";
import { formatDateTime } from "@/utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
} from "@/utils/utils";
import { AuditLogDataType } from "./AuditLog-data";
import StatusChip from "../../../../components/StatusChip";

const AuditLog = () => {
  const { auditLogFilters } = useOutletContext<{ auditLogFilters: any }>();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const isCustomRange = auditLogFilters?.date_range === AUDIT_LOG_CUSTOM_RANGE;
  const { data, isLoading } = useAuditLogList({
    pagination,
    xTENANTID: getXtenantId(),
    ...auditLogFilters,
    dateRangeType: auditLogFilters?.date_range,
    fromDate:
      isCustomRange && auditLogFilters?.startDate
        ? dayjs(auditLogFilters.startDate).startOf("day").toISOString()
        : undefined,
    toDate:
      isCustomRange && auditLogFilters?.endDate
        ? dayjs(auditLogFilters.endDate).endOf("day").toISOString()
        : undefined,
    patient: auditLogFilters?.patient ? auditLogFilters?.patient : undefined,
    user: auditLogFilters?.user?.label,
    category: auditLogFilters?.category?.value,
    logType: auditLogFilters?.logType?.value,
    action: auditLogFilters?.action?.value,
  });

  const COLUMNS = applyDefaultColumnSettings<AuditLogDataType>([
    {
      field: "logTime",
      headerName: "Date & Time",
      flex: 0.6,
      minWidth: 120,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "logUser",
      flex: 0.6,
      headerName: "User",
      minWidth: 150,
      renderCell: (params) => <Box color="primary.main">{params.value}</Box>,
    },
    {
      field: "category",
      flex: 0.8,
      headerName: "Category",
      minWidth: 200,
      renderCell: (params) => (
        <StatusChip label={params.value} bgColor="#e3f2fd" color="#1976d2" />
      ),
    },
    {
      field: "logType",
      flex: 0.5,
      headerName: "Log Type",
      minWidth: 150,
    },
    {
      field: "action",
      flex: 0.4,
      headerName: "Action",
      minWidth: 120,
      renderCell: (params) => (
        <StatusChip variant="default" label={params.value} />
      ),
    },
    {
      field: "activity",
      headerName: "Activity",
      flex: 0.7,
      minWidth: 200,
    },
    {
      field: "patientName",
      flex: 0.6,
      headerName: "Patient",
      minWidth: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "ecarehealthPatientId",
      headerName: "Patient ID",
      flex: 0.3,
      minWidth: 150,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "ipAddress",
      headerName: "IP Address",
      flex: 0.3,
      minWidth: 170,
      renderCell: ({ value }) => value || "-",
    },
  ]);

  return (
    <Box height={"calc(100vh - 160px)"}>
      <CustomDataGridV2
        rows={data?.data?.content ?? []}
        rowCount={data?.data?.page?.totalElements ?? 0}
        loading={isLoading}
        getRowId={(row) => row?.uuid}
        columns={COLUMNS}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
      />
    </Box>
  );
};

export default AuditLog;
